import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { api } from '../lib/api';
import { useFormStore } from '../lib/store';
import { getUserId, getDisplayName, getPictureUrl } from '../lib/liff';
import { validateField } from '../lib/validators';
import ModuleRenderer from '../components/ModuleRenderer';
import StepBar from '../components/StepBar';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { IconAlert, IconChevronDown, IconInfo } from '../components/Icons';
import { Screen, BackButton, Notice, formatNtd, formatDate, daysLeft } from '../components/ui';

/* ---------- step model ---------- */

type StepKey = 'choose' | 'details' | 'confirm';

const STEP_OF_TYPE: Record<string, StepKey> = {
  race_selection: 'choose',
  duo_registration: 'choose',
  shirt_selection: 'choose',
  merchandise: 'choose',
  meal_selection: 'choose',
  pickup_location: 'choose',
  participant: 'details',
  contact: 'details',
  shipping: 'details',
  notes: 'details',
  payment: 'confirm',
  agreement: 'confirm',
};

const STEP_LABEL: Record<StepKey, string> = { choose: '選項目', details: '填資料', confirm: '確認' };
const STEP_ORDER: StepKey[] = ['choose', 'details', 'confirm'];

interface Step {
  key: StepKey;
  label: string;
  modules: any[];
}

function stepOf(module: any): StepKey {
  return STEP_OF_TYPE[module.type] || 'details';
}

function isModuleVisible(module: any, selectedCategoryId: string | null): boolean {
  if (module.showOnlyForCategories && module.showOnlyForCategories.length > 0) {
    return !!selectedCategoryId && module.showOnlyForCategories.includes(selectedCategoryId);
  }
  return true;
}

/* ---------- page ---------- */

export default function EventPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepError, setStepError] = useState<string | null>(null);
  const [descOpen, setDescOpen] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  const {
    event, setEvent, setTheme, reset,
    formData, selectedCategoryId, selectedShippingId, merchandiseItems, participants,
    isSubmitting, setSubmitting, totalAmount, setError, clearAllErrors,
  } = useFormStore();

  useEffect(() => { loadEvent(); }, [eventId, isPreview]);

  async function loadEvent() {
    if (!eventId) { setLoadError('活動連結無效'); setLoading(false); return; }
    setLoading(true);
    setLoadError(null);
    const response = await api.getEvent(eventId, isPreview);
    if (response.success && response.data) {
      reset();
      setEvent(response.data);
      setTheme(response.data.theme || null);
      setStepIndex(0);
    } else {
      setLoadError(response.error || '無法載入活動');
    }
    setLoading(false);
  }

  // Steps are derived from the event's modules; empty steps disappear, the confirm step always exists.
  const steps = useMemo<Step[]>(() => {
    const modules: any[] = (event?.modules || []).filter((m: any) => isModuleVisible(m, selectedCategoryId));
    return STEP_ORDER
      .map((key) => ({ key, label: STEP_LABEL[key], modules: modules.filter((m) => stepOf(m) === key) }))
      .filter((s) => s.modules.length > 0 || s.key === 'confirm');
  }, [event, selectedCategoryId]);

  const step = steps[Math.min(stepIndex, steps.length - 1)];
  const isLast = stepIndex >= steps.length - 1;
  const hasRaceSelection = step?.modules.some((m) => m.type === 'race_selection');

  useEffect(() => {
    setStepError(null);
    // The body is the scroll container (html is overflow:hidden), so scroll both.
    document.body.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [stepIndex]);

  /* ---------- validation ---------- */

  function validateStep(): string[] {
    const missing: string[] = [];
    if (!step) return missing;

    for (const module of step.modules) {
      if (module.type === 'race_selection' && (module.settings?.categories?.length || 0) > 0 && !selectedCategoryId) {
        setError('category', '請選擇一個組別');
        missing.push('category');
      }
      for (const field of module.fields || []) {
        if (field.type === 'image_display') continue;
        const value = formData[field.id];
        const result = validateField(value, { type: field.type, required: field.required, validation: field.validation });
        if (!result.valid) {
          setError(field.id, result.message || `請填寫${field.label}`);
          missing.push(field.id);
        }
      }
    }
    return missing;
  }

  function scrollToField(id: string) {
    const el = document.getElementById(`field-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const focusable = el.querySelector<HTMLElement>('input:not([type=radio]):not([type=checkbox]), select, textarea');
      focusable?.focus({ preventScroll: true });
    }
  }

  function handleNext() {
    clearAllErrors();
    const missing = validateStep();
    if (missing.length > 0) {
      setStepError(missing.length === 1 ? '還有 1 個欄位要填寫' : `還有 ${missing.length} 個欄位要填寫`);
      scrollToField(missing[0]);
      return;
    }
    setStepError(null);
    if (isLast) handleSubmit();
    else setStepIndex((i) => i + 1);
  }

  function handleBack() {
    if (stepIndex > 0) setStepIndex((i) => i - 1);
    else if (window.history.length > 1) navigate(-1);
    else navigate('/');
  }

  async function handleSubmit() {
    if (!event || !eventId) return;
    if (isPreview) { setStepError('預覽模式不會真的送出報名'); return; }
    setSubmitting(true);
    try {
      const response = await api.submitBooking({
        eventId,
        lineUserId: getUserId() || undefined,
        lineDisplayName: getDisplayName() || undefined,
        linePictureUrl: getPictureUrl() || undefined,
        formData,
        selectedCategoryId: selectedCategoryId || undefined,
        selectedShippingId: selectedShippingId || undefined,
        merchandiseItems: merchandiseItems.length > 0 ? merchandiseItems : undefined,
        participants: participants.length > 1 ? participants : undefined,
      });
      if (response.success && response.data) {
        navigate(`/success/${response.data.id}`, { replace: true });
      } else {
        setStepError(response.error || '送出失敗，請再試一次');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setStepError('送出失敗，請確認網路後再試一次');
    } finally {
      setSubmitting(false);
    }
  }

  /* ---------- derived copy ---------- */

  const category = useMemo(() => {
    for (const m of event?.modules || []) {
      if (m.type === 'race_selection') {
        const c = m.settings?.categories?.find((c: any) => c.id === selectedCategoryId);
        if (c) return c;
      }
    }
    return null;
  }, [event, selectedCategoryId]);

  const summaryLine = category
    ? `${category.name} · ${participants.length || 1} 人`
    : merchandiseItems.length > 0 ? `${merchandiseItems.reduce((a, b) => a + b.quantity, 0)} 件商品` : '';

  if (loading) return <LoadingSpinner />;
  if (loadError) return <ErrorMessage message={loadError} onRetry={loadEvent} />;
  if (!event || !step) return <ErrorMessage message="活動不存在或已下架" />;

  const dl = daysLeft(event.registrationDeadline);
  const deadlineText = event.registrationDeadline
    ? dl != null && dl <= 7 && dl >= 0 ? `剩 ${dl} 天` : `${formatDate(event.registrationDeadline, { month: 'numeric', day: 'numeric' })} 截止`
    : null;

  const heading =
    step.key === 'choose' ? (hasRaceSelection ? '你要報哪一組？' : '選擇你要的項目') :
    step.key === 'details' ? '填寫參賽者資料' : '確認並完成報名';
  const subheading =
    step.key === 'choose' ? (hasRaceSelection ? '團報價已含優惠，每人限報一組。' : '可以先不選，之後在群組補。') :
    step.key === 'details' ? '依證件填寫，用於保險與報到。' : '確認金額和須知，送出後再匯款。';
  const nextLabel = !isLast ? `下一步：${steps[stepIndex + 1].key === 'confirm' ? '確認付款' : '填資料'}` : '確認報名';

  return (
    <Screen className="pb-40">
      {/* Header + progress */}
      <div ref={topRef} className="safe-top sticky top-0 z-30 bg-white flex flex-col gap-3.5 px-5 pt-3 pb-4 border-b border-line-soft">
        <div className="flex items-center gap-3">
          <BackButton onClick={handleBack} />
          <div className="flex-1 min-w-0 flex flex-col leading-tight">
            <span className="text-[15px] font-bold truncate">{event.title}</span>
            <span className="text-xs text-muted truncate tabular">
              {[event.eventDate ? formatDate(event.eventDate) : null, deadlineText].filter(Boolean).join(' · ')}
            </span>
          </div>
          <span className="tabular text-[13px] font-bold text-muted shrink-0">{stepIndex + 1} / {steps.length}</span>
        </div>
        <ol aria-label="報名進度" className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
          {steps.map((s, i) => (
            <li key={s.key} aria-current={i === stepIndex ? 'step' : undefined} className="flex flex-col gap-1.5">
              <span className={clsx('h-1.5 rounded-full', i <= stepIndex ? 'bg-primary' : 'bg-line')} />
              <span className={clsx('text-xs', i === stepIndex ? 'font-bold text-ink' : i < stepIndex ? 'text-muted' : 'text-faint')}>{s.label}</span>
            </li>
          ))}
        </ol>
      </div>

      <div key={step.key} className="step-in flex flex-col gap-6 px-5 pt-6">
        {isPreview && (
          <Notice tone="info" icon={<IconInfo size={16} />}>預覽模式：可以走完流程，但不會真的送出。</Notice>
        )}

        <div className="flex flex-col gap-1.5">
          <h1 className="text-2xl font-black leading-snug">{heading}</h1>
          <p className="text-sm text-muted leading-relaxed">{subheading}</p>
        </div>

        {/* Event intro on the first step */}
        {stepIndex === 0 && (event.bannerImageUrl || event.description) && (
          <div className="flex flex-col gap-3">
            {event.bannerImageUrl && (
              <img src={event.bannerImageUrl} alt="" className="w-full aspect-[2/1] object-cover rounded-card" />
            )}
            {event.description && (
              <div className="flex flex-col gap-1">
                <p className={clsx('text-[14px] leading-relaxed text-[#4B5563] whitespace-pre-wrap', !descOpen && 'line-clamp-3')}>
                  {event.description}
                </p>
                {event.description.length > 80 && (
                  <button type="button" onClick={() => setDescOpen((v) => !v)} className="self-start inline-flex items-center gap-1 text-[13px] font-bold text-primary">
                    {descOpen ? '收合' : '看完整說明'}
                    <IconChevronDown size={14} className={clsx('transition-transform', descOpen && 'rotate-180')} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {stepError && (
          <Notice tone="bad" icon={<IconAlert size={16} />}>{stepError}</Notice>
        )}

        {/* Order summary on the final step */}
        {isLast && <OrderSummary event={event} category={category} />}

        {step.modules.map((module: any, index: number) => (
          <ModuleRenderer key={module.id || index} module={module} event={event} />
        ))}

        {isLast && step.modules.length === 0 && (
          <Notice tone="info" icon={<IconInfo size={16} />}>按下「確認報名」就完成了。</Notice>
        )}
      </div>

      <StepBar
        summary={summaryLine}
        amount={totalAmount}
        amountLabel={isLast ? '總計' : '小計'}
        buttonLabel={nextLabel}
        onNext={handleNext}
        loading={isSubmitting}
        final={isLast}
      />
    </Screen>
  );
}

/* ---------- summary ---------- */

function OrderSummary({ event, category }: { event: any; category: any }) {
  const { participants, merchandiseItems, selectedShippingId, subtotal, shippingFee, merchandiseTotal, totalAmount, formData } = useFormStore();
  const count = participants.length || 1;

  const merchLines = useMemo(() => {
    const lines: { name: string; qty: number; amount: number }[] = [];
    for (const m of event?.modules || []) {
      if (m.type !== 'merchandise') continue;
      for (const it of merchandiseItems) {
        const item = m.settings?.items?.find((i: any) => i.id === it.itemId);
        if (item) lines.push({ name: item.name, qty: it.quantity, amount: (item.price || 0) * it.quantity });
      }
    }
    return lines;
  }, [event, merchandiseItems]);

  const shipping = useMemo(() => {
    for (const m of event?.modules || []) {
      const s = m.settings?.shippingOptions?.find((o: any) => o.id === selectedShippingId);
      if (s) return s;
    }
    return null;
  }, [event, selectedShippingId]);

  const shirt = [formData['shirt_type'], formData['shirt_size']].filter(Boolean).join(' / ');
  const hasAnything = category || merchLines.length > 0 || shipping || shirt;

  if (!hasAnything && totalAmount === 0) return null;

  return (
    <section className="flex flex-col rounded-card border border-line divide-y divide-line-soft text-[14px]">
      <div className="px-4 py-3 text-[13px] font-bold text-muted">報名內容</div>
      {category && (
        <Row label={category.name} sub={count > 1 ? `${count} 人` : undefined} amount={subtotal} />
      )}
      {shirt && <Row label="紀念衫" sub={shirt} />}
      {merchLines.map((l) => <Row key={l.name} label={l.name} sub={l.qty > 1 ? `× ${l.qty}` : undefined} amount={l.amount} />)}
      {shipping && <Row label={shipping.label} amount={shippingFee} />}
      <div className="flex items-center justify-between px-4 py-3">
        <span className="font-bold">總計</span>
        <span className="tabular text-lg font-black">{formatNtd(totalAmount)}</span>
      </div>
      {merchandiseTotal > 0 && subtotal === 0 && null}
    </section>
  );
}

function Row({ label, sub, amount }: { label: string; sub?: string; amount?: number }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <span className="min-w-0 flex flex-col leading-snug">
        <span className="font-medium truncate">{label}</span>
        {sub && <span className="text-xs text-muted">{sub}</span>}
      </span>
      {amount != null && <span className="tabular shrink-0">{amount === 0 ? '免費' : formatNtd(amount)}</span>}
    </div>
  );
}
